export type ShortcutPlatform = "mac" | "windows" | "other";
type ShortcutPhase = "idle" | "pending" | "active";
export type ShortcutEffect = "schedule" | "start" | "finish" | "cancel";

export interface ShortcutState {
    platform: ShortcutPlatform;
    phase: ShortcutPhase;
    held: ReadonlySet<string>;
}

export type ShortcutEvent =
    | { type: "keydown"; key: string; repeat: boolean }
    | { type: "keyup"; key: string }
    | { type: "threshold"; elapsedMs: number }
    | { type: "abort" };

export const DICTATION_HOLD_THRESHOLD_MS = 175;

export function createShortcutState(platform: ShortcutPlatform): ShortcutState {
    return { platform, phase: "idle", held: new Set() };
}

function requiredKeys(platform: ShortcutPlatform): string[] {
    return platform === "windows" ? ["Control", "Meta"] : ["Control"];
}

function hasRequiredKeys(state: ShortcutState, held = state.held): boolean {
    return requiredKeys(state.platform).every((key) => held.has(key));
}

export function reduceShortcut(
    state: ShortcutState,
    event: ShortcutEvent,
): { state: ShortcutState; effects: ShortcutEffect[] } {
    if (event.type === "abort") {
        return {
            state: createShortcutState(state.platform),
            effects: state.phase === "active" ? ["cancel"] : [],
        };
    }

    if (event.type === "threshold") {
        if (
            state.phase === "pending" &&
            event.elapsedMs >= DICTATION_HOLD_THRESHOLD_MS &&
            hasRequiredKeys(state)
        ) {
            return { state: { ...state, phase: "active" }, effects: ["start"] };
        }
        return { state, effects: [] };
    }

    if (event.type === "keydown") {
        if (event.repeat) return { state, effects: [] };
        if (event.key === "Escape" && state.phase === "active") {
            return {
                state: createShortcutState(state.platform),
                effects: ["cancel"],
            };
        }

        const required = requiredKeys(state.platform);
        if (!required.includes(event.key)) {
            if (state.phase === "pending" || state.phase === "active") {
                return {
                    state: createShortcutState(state.platform),
                    effects: state.phase === "active" ? ["cancel"] : [],
                };
            }
            return { state, effects: [] };
        }

        const held = new Set(state.held);
        held.add(event.key);
        if (state.phase === "idle" && hasRequiredKeys(state, held)) {
            return {
                state: { ...state, held, phase: "pending" },
                effects: ["schedule"],
            };
        }
        return { state: { ...state, held }, effects: [] };
    }

    const held = new Set(state.held);
    held.delete(event.key);
    if (state.phase === "active" && !hasRequiredKeys(state, held)) {
        return { state: createShortcutState(state.platform), effects: ["finish"] };
    }
    if (state.phase === "pending" && !hasRequiredKeys(state, held)) {
        return { state: createShortcutState(state.platform), effects: [] };
    }
    return { state: { ...state, held }, effects: [] };
}
